import { Fragment, useMemo, useState, type ReactNode } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { antdComparator, columnField, resolveRowId, type AntdColumn } from "./columnAdapter";

// MUI Community DataGrid has no expandable/tree rows, so when antd's `expandable`
// prop is used we render a plain MUI <Table> with manual expand/collapse instead.
// Supports both antd modes: `expandedRowRender` (custom content row) and tree data
// (rows with a `children` array). Controlled via `expandedRowKeys` or internal.

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface AntdExpandable<T = any> {
  expandedRowKeys?: Array<string | number>;
  defaultExpandedRowKeys?: Array<string | number>;
  onExpand?: (expanded: boolean, record: T) => void;
  onExpandedRowsChange?: (keys: readonly (string | number)[]) => void;
  expandedRowRender?: (record: T, index: number) => ReactNode;
  rowExpandable?: (record: T) => boolean;
  childrenColumnName?: string;
}

export interface AntdPaginationLike {
  current?: number;
  pageSize?: number;
  total?: number;
  showTotal?: (total: number, range: [number, number]) => ReactNode;
  [k: string]: any;
}

export interface ExpandableTableProps<T = any> {
  columns: AntdColumn<T>[];
  dataSource: T[];
  rowKey: string | ((r: T) => string | number);
  expandable: AntdExpandable<T>;
  size?: "small" | "middle" | "large";
  pagination?: false | AntdPaginationLike;
  onChange?: (pagination: any, filters: any, sorter: any) => void;
  rowClassName?: string | ((record: T, index: number) => string);
  onRow?: (record: T, index?: number) => { onClick?: () => void; [k: string]: any };
  style?: React.CSSProperties;
  className?: string;
  "data-cy"?: string;
  "data-testid"?: string;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

type SortOrder = "ascend" | "descend";

// antd `sorter: true` sorts by the raw cell value; nulls last.
const compareValues = (a: unknown, b: unknown): number => {
  if (a == null) return b == null ? 0 : 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
};

const getByPath = (record: any, dataIndex: AntdColumn["dataIndex"]): unknown => {
  if (dataIndex == null) return undefined;
  if (Array.isArray(dataIndex)) return dataIndex.reduce((acc: any, k) => acc?.[k], record);
  return record?.[dataIndex as string | number];
};

export function ExpandableTable<T = any>(props: ExpandableTableProps<T>) {
  const {
    columns, dataSource, rowKey, expandable, size, pagination, onChange,
    rowClassName, onRow, style, className,
  } = props;
  const childrenKey = expandable.childrenColumnName ?? "children";
  const getKey = resolveRowId(rowKey);
  const controlled = expandable.expandedRowKeys !== undefined;
  const [internalKeys, setInternalKeys] = useState<Array<string | number>>(
    expandable.defaultExpandedRowKeys ?? []
  );
  const expandedKeys = controlled ? expandable.expandedRowKeys! : internalKeys;

  const toggle = (record: T, key: string | number, wasExpanded: boolean) => {
    const next = wasExpanded ? expandedKeys.filter(k => k !== key) : [...expandedKeys, key];
    if (!controlled) setInternalKeys(next);
    expandable.onExpand?.(!wasExpanded, record);
    expandable.onExpandedRowsChange?.(next);
  };

  const colSpan = columns.length + 1;

  // Pagination: server mode when `total` provided (rows are the current page as-is);
  // otherwise client mode (slice locally). `false` => no pager.
  const pageSize = (pagination && pagination.pageSize) || 25;
  const serverMode = pagination !== false && pagination?.total != null;
  // Client mode seeds from `current` (so a caller can restore it, e.g. from the URL)
  // and reports page changes through onChange like the DataGrid path does.
  const [clientPage, setClientPage] = useState(pagination ? (pagination.current ?? 1) - 1 : 0);
  const page = pagination === false ? 0 : serverMode ? (pagination!.current ?? 1) - 1 : clientPage;

  // antd column sorting (header click cycles ascend -> descend -> off), seeded from
  // `defaultSortOrder`. Server mode only reports the sort; client mode sorts locally.
  const [sort, setSort] = useState<{ idx: number; order: SortOrder } | null>(() => {
    const idx = columns.findIndex(c => c.sorter && c.defaultSortOrder);
    return idx >= 0 ? { idx, order: columns[idx].defaultSortOrder as SortOrder } : null;
  });
  const sorted = useMemo(() => {
    const col = sort && !serverMode ? columns[sort.idx] : undefined;
    if (!sort || !col) return dataSource;
    const cmp = antdComparator(col.sorter) ?? ((a: T, b: T) => compareValues(getByPath(a, col.dataIndex), getByPath(b, col.dataIndex)));
    const dir = sort.order === "ascend" ? 1 : -1;
    return [...dataSource].sort((a, b) => dir * cmp(a, b));
  }, [dataSource, columns, sort, serverMode]);
  const onSortClick = (idx: number) => {
    const next: SortOrder | null =
      sort?.idx !== idx ? "ascend" : sort.order === "ascend" ? "descend" : null;
    setSort(next ? { idx, order: next } : null);
    const field = columnField(columns[idx], idx);
    onChange?.({ ...(pagination || {}), current: page + 1, pageSize }, {}, next ? { field, columnKey: field, order: next } : { order: null });
  };

  const rows =
    pagination === false || serverMode
      ? sorted
      : sorted.slice(page * pageSize, page * pageSize + pageSize);

  const renderRow = (record: T, index: number, depth: number): ReactNode => {
    const key = getKey(record);
    const kids = (record as any)[childrenKey] as T[] | undefined;
    const hasTreeChildren = Array.isArray(kids);
    const canExpand = expandable.expandedRowRender
      ? expandable.rowExpandable?.(record) ?? true
      : hasTreeChildren;
    const isExpanded = expandedKeys.includes(key);
    const rowAttrs = onRow?.(record, index);
    const rowClick = rowAttrs?.onClick;
    const cls = typeof rowClassName === "function" ? rowClassName(record, index) : rowClassName;
    return (
      <Fragment key={key}>
        <TableRow
          data-id={key}
          data-cy="expandable-row"
          hover
          className={cls}
          onClick={rowClick}
          // antd onRow returns a full-row style (the allocation grid uses it for the
          // group/subtotal band); apply it to the row so the band is continuous
          // rather than dropped. Was previously only reading onClick.
          style={rowAttrs?.style}
          sx={rowClick ? { cursor: "pointer" } : undefined}
        >
          <TableCell padding="checkbox" sx={{ pl: depth * 3 }}>
            {canExpand && (
              <IconButton
                size="small"
                aria-label={isExpanded ? "Collapse row" : "Expand row"}
                data-cy="row-expand-toggle"
                onClick={e => {
                  e.stopPropagation();
                  toggle(record, key, isExpanded);
                }}
              >
                {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
              </IconButton>
            )}
          </TableCell>
          {columns.map((col, ci) => {
            const value = getByPath(record, col.dataIndex);
            return (
              <TableCell key={columnField(col, ci)} align={col.align}>
                {(col.render ? col.render(value, record, index) : (value as ReactNode)) ?? null}
              </TableCell>
            );
          })}
        </TableRow>
        {isExpanded && expandable.expandedRowRender && (
          <TableRow data-cy="expanded-row">
            <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: "unset" }}>
              <Collapse in unmountOnExit>
                <Box sx={{ p: 1.5 }}>{expandable.expandedRowRender(record, index)}</Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
        {isExpanded && hasTreeChildren && kids!.map((child, ci) => renderRow(child, ci, depth + 1))}
      </Fragment>
    );
  };

  return (
    <Box style={style} className={className} data-cy={props["data-cy"]} data-testid={props["data-testid"]}>
      <TableContainer component={Paper} variant="outlined">
        <Table size={size === "small" ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              {columns.map((col, ci) => (
                <TableCell key={columnField(col, ci)} align={col.align} sortDirection={sort?.idx === ci ? (sort.order === "ascend" ? "asc" : "desc") : false}>
                  {col.sorter ? (
                    <TableSortLabel
                      active={sort?.idx === ci}
                      direction={sort?.idx === ci && sort.order === "descend" ? "desc" : "asc"}
                      onClick={() => onSortClick(ci)}
                    >
                      {col.title as ReactNode}
                    </TableSortLabel>
                  ) : (
                    (col.title as ReactNode)
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{rows.map((r, i) => renderRow(r, i, 0))}</TableBody>
        </Table>
      </TableContainer>
      {pagination !== false && (
        <>
          {pagination?.showTotal && (
            <Box sx={{ px: 2, py: 0.5, fontSize: 13, color: "text.secondary" }} data-cy="pagination-total">
              {pagination.showTotal(
                serverMode ? pagination.total ?? 0 : dataSource.length,
                [page * pageSize + 1, page * pageSize + rows.length]
              )}
            </Box>
          )}
          <TablePagination
            component="div"
            count={serverMode ? pagination!.total! : dataSource.length}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[]}
            onPageChange={(_e, newPage) => {
              if (!serverMode) setClientPage(newPage);
              onChange?.({ ...pagination, current: newPage + 1, pageSize }, {}, {});
            }}
          />
        </>
      )}
    </Box>
  );
}

export default ExpandableTable;
